import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import studentAuthService from "../services/studentAuthService";

const StudentAuthContext = createContext(null);

export function StudentAuthProvider({ children }) {
  const [account, setAccount] = useState(studentAuthService.getAccount());
  const [hasIntroVideo, setHasIntroVideo] = useState(false);

  const refreshAccount = useCallback(async () => {
    if (!studentAuthService.getToken()) return;
    try {
      const res = await studentAuthService.me();
      setHasIntroVideo(!!res.data.has_intro_video);
    } catch {
      // token likely expired/invalid — leave state as-is, route guards handle redirecting to login
    }
  }, []);

  useEffect(() => {
    refreshAccount();
  }, [refreshAccount]);

  // Signup no longer logs the student in directly — it creates an
  // unverified account and emails an OTP. The caller should navigate
  // to the OTP screen using the returned { email, requiresVerification }.
  const signup = useCallback(async (payload) => {
    const res = await studentAuthService.signup(payload);
    return res.data; // { email, requiresVerification: true }
  }, []);

  // The only two places a real session actually starts.
  const login = useCallback(
    async (payload) => {
      const res = await studentAuthService.login(payload);
      studentAuthService.saveSession(res.data);
      setAccount(res.data.account);
      await refreshAccount();
      return res.data.account;
    },
    [refreshAccount]
  );

  const verifyOtp = useCallback(
    async (payload) => {
      const res = await studentAuthService.verifyOtp(payload);
      studentAuthService.saveSession(res.data);
      setAccount(res.data.account);
      await refreshAccount();
      return res.data.account;
    },
    [refreshAccount]
  );

  const resendOtp = useCallback(async (payload) => {
    return studentAuthService.resendOtp(payload);
  }, []);

  const logout = useCallback(() => {
    studentAuthService.logout();
    setAccount(null);
    setHasIntroVideo(false);
  }, []);

  return (
    <StudentAuthContext.Provider
      value={{
        account,
        isAuthenticated: !!account,
        hasIntroVideo,
        refreshAccount,
        login,
        signup,
        verifyOtp,
        resendOtp,
        logout,
      }}
    >
      {children}
    </StudentAuthContext.Provider>
  );
}

export function useStudentAuth() {
  const ctx = useContext(StudentAuthContext);
  if (!ctx) throw new Error("useStudentAuth must be used within StudentAuthProvider");
  return ctx;
}
